import { SECTION_OPTION_SEGMENT } from "./segment";

export const VARIABLE_TARGET_OBJECT = [
     { value: "Lead", label: "Lead" },
     { value: "Contact", label: "Contact" },
     { value: "Account", label: "Account" },
     { value: "ParentAccount", label: "Parent Account" },
     { value: "Opportunity", label: "Opportunity" },
     { value: "Quote", label: "Quote" },
     { value: "Order", label: "Order" },
     { value: "Contract", label: "Contract" },
     { value: "Campaign", label: "Campaign" },
     { value: "Event", label: "Event" },
];

// Declared ahead of VARIABLE_TRIGGER_TYPES (rather than next to LL_OBJECT_SOURCE_ID
// below) because that array references it at module-eval time.
export const REVEALED_REALTIME_TRIGGER_TYPE = "revealed-realtime";

export const VARIABLE_TRIGGER_TYPES = [
     { value: "is-created", label: "is created" },
     { value: "related", label: "becomes related to" },
     { value: "field-matching", label: "has a field matching a rule" },
     { value: "saved-report", label: "appears in a saved report" },
     { value: "being-active", label: "being active" },
     { value: REVEALED_REALTIME_TRIGGER_TYPE, label: "is revealed (real time)" },
];

// Rollout gate for the realtime reveal trigger. The lambdas are account-agnostic —
// this list is the only thing limiting who can create one.
export const REALTIME_REVEAL_ACCOUNT_IDS = [
     "db63dfdd-b46d-417b-8222-768c449b238f", // Bully Dog
     "3f96c102-e1b9-4bee-8c9c-57b73cba97ea", // SCT
     "dcf4fd9d-f190-4907-b83d-e11626c34f0d", // prod test account
     "e19758b6-e7c7-412f-8ff9-4c27793e0c00", // staging test account
     "c52e8b22-660b-4d36-8a94-6c0c93860d65", // dev test account
     "5a1e7871-2090-4b39-843f-812e941443c8", // HWV: The Winston
     "54ad9316-40fe-4086-89ff-c4a97c568ebb", // HWV: Vinland Hotel & Lounge
     "ac6fea2f-a271-4e6e-9517-4a41730abb61", // HWV: Costanoa
     "21950dc2-6bda-4614-b1aa-88fde9a71e2d", // HWV: Sideways Inn
     "0e6a2d92-450b-4907-903b-f092a81d4d72", // HWV: Borrego
     "62908a7c-9bb4-4e36-a8c6-5187b64db357", // HWV: Flying Flags
     "fabb4c57-06bf-4d61-b773-abec78151f2d", // HWV: Palm Canyon
     "0213a1c9-2414-4c47-8a52-749582e1c7d7", // HWV: Flying Flags Avila Beach
];

export const LL_OBJECT_SOURCE_ID = "67b40000-bd95-43f4-9122-b0d4d63be6d0";

// The People properties a realtime reveal trigger can filter on. The realtime lambda
// (HandleRealtimeRevealedPerson) evaluates a rule's filter against a record it builds from
// the Postgres CentralizedPerson row, not from BigQuery, so it can only see these three.
// Offering any other property would let a user save a filter the realtime path cannot
// apply -- and the person would be delivered as if the filter were not there.
export const REALTIME_REVEAL_FILTER_PROPERTIES = ["name", "email", "enrichType"];

// Narrows a property/metric option list to what a realtime reveal trigger supports.
// A no-op for every other trigger type.
export const filterRealtimeRevealProperties = (options, triggerType) =>
     triggerType === REVEALED_REALTIME_TRIGGER_TYPE
          ? (options || []).filter((option) => REALTIME_REVEAL_FILTER_PROPERTIES.includes(option?.value))
          : options;

// The realtime reveal trigger is People-only, ListenLayer-source-only, and limited to
// the rollout accounts. Every other trigger type ignores this.
export const canUseRealtimeReveal = ({ targetObject, externalId, accountId }) =>
     targetObject === "People" && externalId === LL_OBJECT_SOURCE_ID && REALTIME_REVEAL_ACCOUNT_IDS.includes(accountId);

// Which optional steps a trigger type puts in the wizard. ConditionStep keeps the
// trigger type in local draft state and only commits it to the parent on SAVE AND NEXT,
// so these flags are pushed up from the select itself — otherwise the collapsed step
// cards below would keep the previous type's numbering until the user saved.
export const getStepVisibilityFlags = (triggerType) => ({
     isSchedule: triggerType === "saved-report",
     isRealtimeReveal: triggerType === REVEALED_REALTIME_TRIGGER_TYPE,
});

// The server-side-trigger wizard is not a fixed four steps: the schedule step only
// exists for saved-report triggers, and the Test & Preview step is skipped entirely for
// a realtime reveal trigger (there is no historical set to sample). Steps 1 and 2 —
// Manage Type and Conditions to Match — are always 1 and 2. A null means the step is
// not rendered at all. Single source of truth so the numbers cannot drift apart.
export const getServerSideTriggerStepNumbers = ({ isSchedule = false, isRealtimeReveal = false } = {}) => {
     const schedule = isSchedule ? 3 : null;
     const preview = isRealtimeReveal ? null : isSchedule ? 4 : 3;
     const name = (preview !== null ? preview : schedule !== null ? schedule : 2) + 1;

     return { schedule, preview, name };
};

export const VARIABLE_OPERATORS_PROPERTY = [
     { value: "ct", label: "contains" },
     { value: "nct", label: "doesn't contain" },
     { value: 'bw', label: 'begins with' },
     { value: 'ew', label: 'ends with' },
     { value: 'ieon', label: 'is empty or null' },
     { value: 'ine', label: 'is not empty' },
     { value: "em", label: "exactly match" },
     { value: "isnot", label: "is not" },
];

export const VARIABLE_OPERATORS_LL = [
     { value: "ct", label: "contains" },
     { value: "nct", label: "not contain" },
     { value: "em", label: "equals to" },
     { value: "nem", label: "not equals to" },
];

export const VARIABLE_OPERATORS_LL_NUMBER = [
     { value: 'eq', label: 'equals to' },
     { value: "neq", label: "not equals to" },
     { value: 'gtoet', label: 'greater than or equal to' },
     { value: 'ltoet', label: 'less than or equal to' },
     { value: 'gt', label: 'is greater than' },
     { value: 'lt', label: 'is less than' },
];

export const VARIABLE_OPERATORS_NOT_EMPTY = [
     { value: 'ine', label: 'exists with any value' },
]

export const VARIABLE_OPERATORS_EMPTY = [
     { value: 'ieon', label: 'is empty or null' },
     { value: 'ine', label: 'exists with any value' },
]

export const VARIABLE_OPERATORS_CHECKLIST = [
     { value: 'checklist', label: 'checklist' },
     { value: 'nctChecklist', label: 'not contain checklist' }
];

export const VARIABLE_OPERATORS_NOT_ARRAY = [
     { value: "ct", label: "contains" },
     { value: "nct", label: "doesn't contain" },
     { value: 'ieon', label: 'is empty or null' },
     { value: 'ine', label: 'is not empty' },
     { value: "em", label: "exactly match" },
];

export const VARIABLE_OPERATORS_EMAIL_LIST = [
     { value: "ct", label: "contains" },
     { value: "nct", label: "not contain" },
     { value: "em", label: "equals to" },
     { value: "nem", label: "not equals to" },
];

export const VARIABLE_OPERATORS_RELATIONSHIP = [
     { value: "ct", label: "contains" },
     { value: "nct", label: "doesn't contain" },
     { value: 'ieon', label: 'is empty or null' },
     { value: 'ine', label: 'is not empty' },
     { value: "isnot", label: "is not" },
];

export const VARIABLE_OPERATORS_METRIC = [
     { value: 'eq', label: 'equals to' },
     { value: 'gtoet', label: 'greater than or equal to' },
     { value: 'ltoet', label: 'less than or equal to' },
     { value: 'gt', label: 'is greater than' },
     { value: 'lt', label: 'is less than' },
];

export const VARIABLE_OPERATORS_BOOLEAN = [
     { value: "is", label: "true" },
     { value: "ins", label: "false" },
];

export const VARIABLE_OPERATORS_YES_NO = [
     { value: "is", label: "Yes" },
     { value: "ins", label: "No" },
];

export const VARIABLE_OPERATORS_DATE = [
     { value: 'ieon', label: 'is empty or null' },
     { value: 'on', label: 'Is on' },
     { value: 'bf', label: 'Is before' },
     { value: 'af', label: 'Is after' },
     { value: 'ine', label: 'is not empty' },
]

export const FILTER_CONDITION = {
     ct: { value: "ct", label: "contains" },
     nct: { value: "nct", label: "doesn't contain" },
     bw: { value: "bw", label: "begins with" },
     ew: { value: "ew", label: "ends with" },
     ieon: { value: "ieon", label: "is empty or null" },
     ine: { value: "ine", label: "is not empty" },
     em: { value: "em", label: "exactly match" },
     isnot: { value: "isnot", label: "is not" },
     exav: { value: "exav", label: "exists with any value" },
     eq: { value: "eq", label: "equals to" },
     gtoet: { value: "gtoet", label: "greater than or equal to" },
     ltoet: { value: "ltoet", label: "less than or equal to" },
     gt: { value: "gt", label: "is greater than" },
     lt: { value: "lt", label: "is less than" },
     is: { value: "is", label: "true" },
     ins: { value: "ins", label: "false" },
     on: { value: 'on', label: 'Is on' },
     bf: { value: 'bf', label: 'Is before' },
     af: { value: 'af', label: 'Is after' },
};

export const SEVER_SIDE_OBJECT = {
     LEAD: "Lead",
     CONTACT: "Contact",
     ACCOUNT: "Account",
     PARENT_ACCOUNT: "ParentAccount",
     OPPORTUNITY: "Opportunity",
     QUOTE: "Quote",
     ORDER: "Order",
     CONTRACT: "Contract",
     CAMPAIGN: "Campaign",
     EVENT: "Event",
     PEOPLE: "People",
     COMPANY: "Company",
     SALES_CONVERSION_EVENT: "Sales Conversion Event",
};

export const SEVER_SIDE_OBJECT_DATA = {
     [SEVER_SIDE_OBJECT.LEAD]: {
          property: [
               { value: "Status", label: "Status" },
               { value: "IsConverted", label: "IsConverted" },
               { value: "IsQualified", label: "IsQualified" },
               { value: "IsCustomer", label: "IsCustomer" },
               { value: "Rating", label: "Rating" },
          ],
          metric: [
               { value: "EngagedScore", label: "EngagedScore" },
               { value: "FitScore", label: "FitScore" },
          ],
     },
     [SEVER_SIDE_OBJECT.CONTACT]: {
          property: [
               { value: "Status", label: "Status" },
               { value: "IsQualified", label: "IsQualified" },
               { value: "IsCustomer", label: "IsCustomer" },
          ],
          metric: [
               { value: "EngagedScore", label: "EngagedScore" },
               { value: "FitScore", label: "FitScore" },
          ],
     },
     [SEVER_SIDE_OBJECT.ACCOUNT]: {
          property: [
               { value: "Status", label: "Status" },
               { value: "IsQualified", label: "IsQualified" },
               { value: "IsCustomer", label: "IsCustomer" },
               { value: "Rating", label: "Rating" },
               { value: "IsPartner", label: "IsPartner" },
               { value: "Type", label: "Type" },
          ],
          metric: [
               { value: "EngagedScore", label: "EngagedScore" },
               { value: "FitScore", label: "FitScore" },
          ],
     },
     [SEVER_SIDE_OBJECT.PARENT_ACCOUNT]: {
          property: [
               { value: "Status", label: "Status" },
               { value: "IsQualified", label: "IsQualified" },
               { value: "IsCustomer", label: "IsCustomer" },
               { value: "Rating", label: "Rating" },
               { value: "IsPartner", label: "IsPartner" },
               { value: "Type", label: "Type" },
          ],
          metric: [
               { value: "EngagedScore", label: "EngagedScore" },
               { value: "FitScore", label: "FitScore" },
          ],
     },
     [SEVER_SIDE_OBJECT.OPPORTUNITY]: {
          property: [
               { value: "Status", label: "Status" },
               { value: "IsQualified", label: "IsQualified" },
               { value: "StageName", label: "StageName" },
               { value: "IsWon", label: "IsWon" },
               { value: "IsClosed", label: "IsClosed" },
               { value: "CloseDate", label: "CloseDate" },
               { value: "IsCustomer", label: "IsCustomer" },
               { value: "Currency", label: "Currency" },
               { value: "Type", label: "Type" },
          ],
          metric: [
               { value: "EngagedScore", label: "EngagedScore" },
               { value: "FitScore", label: "FitScore" },
               { value: "AgeInDays", label: "AgeInDays" },
          ],
     },
     [SEVER_SIDE_OBJECT.QUOTE]: {
          property: [
               { value: "Status", label: "Status" },
               { value: "IsAccepted", label: "IsAccepted" },
               { value: "Currency", label: "Currency" },
          ],
          metric: [],
     },
     [SEVER_SIDE_OBJECT.ORDER]: {
          property: [
               { value: "Status", label: "Status" },
               { value: "Type", label: "Type" },
               { value: "Currency", label: "Currency" },
          ],
          metric: [],
     },
     [SEVER_SIDE_OBJECT.CONTRACT]: {
          property: [{ value: "Status", label: "Status" }],
          metric: [],
     },
     [SEVER_SIDE_OBJECT.CAMPAIGN]: {
          property: [
               { value: "Status", label: "Status" },
               { value: "Type", label: "Type" },
          ],
          metric: [],
     },
     [SEVER_SIDE_OBJECT.EVENT]: {
          property: [{ value: "Type", label: "Type" }],
          metric: [],
     },
};

export const VARIABLE_RELATED_OBJECT = {
     [SEVER_SIDE_OBJECT.LEAD]: [
          { value: "Contact", label: "Contact" },
          { value: "Account", label: "Account" },
          { value: "Opportunity", label: "Opportunity" },
          { value: "Campaign", label: "Campaign" },
          { value: "Event", label: "Event" },
     ],
     [SEVER_SIDE_OBJECT.CONTACT]: [
          { value: "Lead", label: "Lead" },
          { value: "Account", label: "Account" },
          { value: "Opportunity", label: "Opportunity" },
          { value: "Campaign", label: "Campaign" },
          { value: "Quote", label: "Quote" },
          { value: "Order", label: "Order" },
          { value: "Contract", label: "Contract" },
          { value: "Event", label: "Event" },
     ],
     [SEVER_SIDE_OBJECT.ACCOUNT]: [
          { value: "ParentAccount", label: "ParentAccount" },
          { value: "Lead", label: "Lead" },
          { value: "Contact", label: "Contact" },
          { value: "Opportunity", label: "Opportunity" },
          { value: "Contract", label: "Contract" },
          { value: "Quote", label: "Quote" },
          { value: "Order", label: "Order" },
          { value: "Event", label: "Event" },
     ],
     [SEVER_SIDE_OBJECT.PARENT_ACCOUNT]: [
          { value: "Account", label: "Account" },
     ],
     [SEVER_SIDE_OBJECT.OPPORTUNITY]: [
          { value: "Lead", label: "Lead" },
          { value: "Contract", label: "Contract" },
          { value: "Account", label: "Account" },
          { value: "Contact", label: "Contact" },
          { value: "Campaign", label: "Campaign" },
          { value: "Quote", label: "Quote" },
          { value: "Order", label: "Order" },
          { value: "Event", label: "Event" },
     ],
     [SEVER_SIDE_OBJECT.QUOTE]: [
          { value: "Contact", label: "Contact" },
          { value: "Order", label: "Order" },
          { value: "Contract", label: "Contract" },
          { value: "Account", label: "Account" },
          { value: "Opportunity", label: "Opportunity" },
          { value: "Event", label: "Event" },
     ],
     [SEVER_SIDE_OBJECT.ORDER]: [
          { value: "Account", label: "Account" },
          { value: "Quote", label: "Quote" },
          { value: "Contract", label: "Contract" },
          { value: "Opportunity", label: "Opportunity" },
          { value: "Contact", label: "Contact" },
          { value: "Event", label: "Event" },
     ],
     [SEVER_SIDE_OBJECT.CONTRACT]: [
          { value: "Account", label: "Account" },
          { value: "Contact", label: "Contact" },
          { value: "Opportunity", label: "Opportunity" },
          { value: "Quote", label: "Quote" },
          { value: "Order", label: "Order" },
          { value: "Event", label: "Event" },
     ],
     [SEVER_SIDE_OBJECT.CAMPAIGN]: [
          { value: "Lead", label: "Lead" },
          { value: "Contact", label: "Contact" },
          { value: "Opportunity", label: "Opportunity" },
          { value: "Event", label: "Event" },
     ],
     [SEVER_SIDE_OBJECT.EVENT]: [
          { value: "Lead", label: "Lead" },
          { value: "Contact", label: "Contact" },
          { value: "Account", label: "Account" },
          { value: "Campaign", label: "Campaign" },
          { value: "Opportunity", label: "Opportunity" },
          { value: "Order", label: "Order" },
          { value: "Quote", label: "Quote" },
          { value: "Contract", label: "Contract" },
     ],
};

export const VARIABLE_SEVER_SIDE_OBJECT = [
     { value: "targetObject", label: "Target Object" },
     { value: "relatedObject", label: "Related Object" },
];

export const LISTEN_LL_OBJECT_PROPERTY_METRIC = {
     [SEVER_SIDE_OBJECT.PEOPLE]: [
          // Person Data
          { value: "name", label: "Full Name", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA },
          { value: "email", label: "Primary Email", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA },
          { value: "emailList", label: "All Email Addresses", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, type: 'array' },
          { value: "phone", label: "Primary Phone", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA },
          { value: "phoneList", label: "Phone List", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, type: 'array' },
          { value: "externalId", label: "External ID", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA },
          { value: "dataSources", label: "Data Sources", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, condition: 'checkList' },
          { value: "revealedZipCode", label: "Revealed Zip Code", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA },
          { value: "emailType", label: "Email Type", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA },
          { value: "enrichType", label: "Enrichment", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, condition: 'checkList' },

          { value: "currentTitle", label: "Current Title", section: SECTION_OPTION_SEGMENT.COMPANIES },
          { value: "currentCompany", label: "Current Company", section: SECTION_OPTION_SEGMENT.COMPANIES },
          { value: "relatedCompanies", label: "Related Companies", section: SECTION_OPTION_SEGMENT.COMPANIES },
          { value: "otherRoles", label: "Other Roles", section: SECTION_OPTION_SEGMENT.COMPANIES },
          { value: "linkedinSummary", label: "LinkedIn Summary", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA },
          { value: "skills", label: "Skills", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, type: 'array', condition: 'checkList' },
          { value: "volunteering", label: "Volunteering", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, type: 'array' },
          { value: "certifications", label: "Certifications", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, type: 'array' },
          { value: "education", label: "Education", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, type: 'array' },
          { value: "projects", label: "Projects", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, type: 'array' },
          { value: "recommendations", label: "Recommendations", section: SECTION_OPTION_SEGMENT.PERSONAL_DATA, type: 'array' },
          { value: "currentCompanyPrimaryIndustry", label: "Current Company Primary Industry", section: SECTION_OPTION_SEGMENT.COMPANIES, condition: 'checkList' },
          { value: "currentCompanyRevenue", label: "Current Company Revenue", section: SECTION_OPTION_SEGMENT.COMPANIES, condition: 'checkList' },
          { value: 'currentCompanyDomain', label: 'Current Company Domain', section: SECTION_OPTION_SEGMENT.COMPANIES },
          { value: 'employees', label: 'Current Company Total Employees', section: SECTION_OPTION_SEGMENT.COMPANIES, condition: 'checkList' },
          { value: 'industries', label: 'Current Company Products & Services Tags', section: SECTION_OPTION_SEGMENT.COMPANIES, condition: 'checkList' },

          { value: 'channelFirst', label: 'Channel - First', section: SECTION_OPTION_SEGMENT.CHANNEL, condition: 'checkList' },
          { value: 'channelLast', label: 'Channel - Last', section: SECTION_OPTION_SEGMENT.CHANNEL, condition: 'checkList' },
          { value: 'channelPath', label: 'Channel - Path', section: SECTION_OPTION_SEGMENT.CHANNEL, condition: 'checkList' },

          // Person Metric
          { value: 'sourcePathCount', label: 'Source Path Count', section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_DETAILS, type: 'number' },
          { value: 'noneDirectSourcePathCount', label: 'Non-Direct Source Path Count', section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_DETAILS, type: 'number' },

          { value: 'identifiedSourcePathCount', label: 'Created Source Path Count', section: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO, type: 'number'  },
          { value: 'identifiedSourcePathCountND', label: 'Created Source Path Count (non-direct)', section: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO, type: 'number'  },
          
          { value: 'engagedSessionCount', label: 'Engaged Sessions', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'engagementScore', label: 'Lifetime Engagement Score', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'pageViewCount', label: 'Pageviews', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'sessionCount', label: 'Sessions', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'avgPageViewsPerSession', label: 'Avg Pageviews / Session', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'avgSessionDuration', label: 'Avg Session Duration', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'avgVisibleTimePerPage', label: 'Avg Visible Time / Page', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'bouncedSessionCount', label: 'Bounced Sessions', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },

          { value: 'conversionCount', label: 'All Conversions', section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS, type: 'number' },
          { value: 'primaryConversionCount', label: 'Primary Conversions', section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS, type: 'number' },
          { value: 'secondaryConversionCount', label: 'Secondary Conversions', section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS, type: 'number' },

          // Person Created Source Info
          { value: "userSourceFirstPersonCreated", label: "User Source - First (person created)", section: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE, condition: 'checkList' },
          { value: "userSourcePathPersonCreated", label: "User Source - Path (person created)", section: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE, condition: 'checkList' },
          { value: "userSourceLastPersonCreated", label: "User Source - Last (person created)", section: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE, condition: 'checkList' },
          { value: "landingPageOriginPath", label: "Landing Page Origin Path", section: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },
          { value: "exitPageOriginPath", label: "Exit Page Origin Path", section: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },

          { value: "referrerHostname", label: "Referrer Hostname", section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },
          { value: "referrerOriginPath", label: "Referrer Origin Path", section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },
          { value: "referrerPath", label: "Referrer Path", section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },

          { value: "utmSource", label: "utm_source", section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },
          { value: "utmMedium", label: "utm_medium", section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },
          { value: "utmCampaign", label: "utm_campaign", section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },
          { value: "utmTerm", label: "utm_term", section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },
          { value: "utmContent", label: "utm_content", section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.CREATED_SOURCE },

          { value: "userSourceFirst", label: "User Source - First (person)", section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY, condition: 'checkList' },
          { value: "userSourcePath", label: "User Source - Path (person)", section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY, condition: 'checkList' },
          { value: "userSourceLast", label: "User Source - Last (person)", section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY, condition: 'checkList' },

          // Date
          { value: "revealedDate", label: "Revealed Date", section: SECTION_OPTION_SEGMENT.DATE, type: 'date' },
          { value: "providedDate", label: "First Provided Date", section: SECTION_OPTION_SEGMENT.DATE, type: 'date' },
          { value: "importedDate", label: "Imported Date", section: SECTION_OPTION_SEGMENT.DATE, type: 'date' },
          { value: "firstActiveAt", label: "First Active Date", section: SECTION_OPTION_SEGMENT.DATE, type: 'date' },
          // Target Account
          { value: "isTargetAccount", label: "Is Target Account", type: 'boolean', section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT },
          { value: "targetAccountGroups", label: "Target Account Groups", section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT, condition: 'checkList' },

     ],
     [SEVER_SIDE_OBJECT.COMPANY]: [
          // Company Information
          { value: "companyName", label: "Company Name", section: SECTION_OPTION_SEGMENT.GENERAL_INFORMATION },
          { value: "domain", label: "Domain", section: SECTION_OPTION_SEGMENT.GENERAL_INFORMATION },
          { value: "dataSources", label: "Data Sources", section: SECTION_OPTION_SEGMENT.GENERAL_INFORMATION, condition: 'checkList' },
          { value: "businessType", label: "Business Type", section: SECTION_OPTION_SEGMENT.GENERAL_INFORMATION },
          
          { value: "companyState", label: "State", section: SECTION_OPTION_SEGMENT.LOCATION, condition: 'checkList' },
          // { value: "city", label: "City", section: SECTION_OPTION_SEGMENT.LOCATION },
          { value: "companyCountry", label: "Country", section: SECTION_OPTION_SEGMENT.LOCATION, condition: 'checkList' },
          
          { value: 'channelFirst', label: 'Channel - First', section: SECTION_OPTION_SEGMENT.CHANNEL, condition: 'checkList' },
          { value: 'channelLast', label: 'Channel - Last', section: SECTION_OPTION_SEGMENT.CHANNEL, condition: 'checkList' },
          { value: 'channelPath', label: 'Channel - Path', section: SECTION_OPTION_SEGMENT.CHANNEL, condition: 'checkList' },
          
          { value: "revenue", label: "Revenue", section: SECTION_OPTION_SEGMENT.SIZE, condition: 'checkList' },
          { value: "employees", label: "Total Employees", section: SECTION_OPTION_SEGMENT.SIZE, condition: 'checkList' },
          { value: "monthlyVisitors", label: "Monthly Visitor", section: SECTION_OPTION_SEGMENT.SIZE, condition: 'checkList' },
          { value: "yearFounded", label: "Year Founded", section: SECTION_OPTION_SEGMENT.SIZE },
          
          { value: "primaryIndustry", label: "Primary Industry", section: SECTION_OPTION_SEGMENT.INDUSTRY, condition: 'checkList' },
          { value: "industries", label: "Products & Services Tags", section: SECTION_OPTION_SEGMENT.INDUSTRY, type: 'array', condition: 'checkList' },
          
          // Company Metric
          { value: "sourcePathCount", label: "Source Path Count", section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_DETAILS, type: 'number' },
          { value: "noneDirectSourcePathCount", label: "Non-Direct Source Path Count", section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_DETAILS, type: 'number' },

          { value: "peopleCount", label: "People", section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: "userCount", label: "Users", section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: "sessionCount", label: "Sessions", section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: "engagedSessionCount", label: "Engaged Sessions", section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'engagementScore', label: 'Lifetime Engagement Score', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'avgSessionDuration', label: 'Avg Session Duration', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },
          { value: 'avgVisibleTimePerPage', label: 'Avg Visible Time / Page', section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY, type: 'number' },

          { value: "conversionCount", label: "All Conversions", section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS, type: 'number' },
          { value: "primaryConversionCount", label: "Primary Conversions", section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS, type: 'number' },
          { value: "secondaryConversionCount", label: "Secondary Conversions", section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS, type: 'number' },

          // User Source
          { value: "createdSourceFirst", label: "User Source - First (company)", section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE, condition: 'checkList' },
          { value: "createdSourcePath", label: "User Source - Path (company)", section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE, condition: 'checkList' },
          { value: "revealedSource", label: "User Source - Last (company)", section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE, condition: 'checkList' },
          
          { value: "landingPageOriginPath", label: "Landing Page Origin Path", section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },
          { value: "exitPageOriginPath", label: "Exit Page Origin Path", section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },

          { value: "referrerHostname", label: 'Referrer Hostname', section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },
          { value: "referrerOriginPath", label: 'Referrer Origin Path', section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },
          { value: "referrerPath", label: 'Referrer Path', section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },

          { value: "utmSource", label: 'utm_source', section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },
          { value: "utmCampaign", label: 'utm_campaign', section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },
          { value: "utmMedium", label: 'utm_medium', section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },
          { value: "utmTerm", label: 'utm_term', section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },
          { value: "utmContent", label: 'utm_content', section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE },

          // Date
          { value: "createdDate", label: "Creation date", type: 'date', section: SECTION_OPTION_SEGMENT.DATE },
          { value: "dataSourcesRevealedDate", label: "Revealed Date", section: SECTION_OPTION_SEGMENT.DATE, type: 'date' },
          { value: "dataSourcesProvidedDate", label: "First Provided Date", section: SECTION_OPTION_SEGMENT.DATE, type: 'date' },
          { value: "dataSourcesImportedDate", label: "Imported Date", section: SECTION_OPTION_SEGMENT.DATE, type: 'date' },

          // Target Account
          { value: "isTargetAccount", label: "Is Target Account", type: 'boolean', section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT },
          { value: "targetAccountGroups", label: "Target Account Groups", section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT, condition: 'checkList' },
     ]
}

export const CHECKLIST_OPTION = { value: 'checklist', label: 'checklist' };
export const NOT_CHECK_LIST_OPTION = { value: 'nctChecklist', label: 'not contain checklist' };

export const VARIABLE_VALUE_SUPPORT_DATA = [
     { value: "true", label: "true" },
     { value: "false", label: "false" },
];

export const VARIABLE_VALUE_SUPPORT_LL_OBJECT = [
     { value: "Provided", label: "Provided" },
     { value: "Revealed", label: "Revealed" },
     { value: "Imported", label: "Imported" }
]

export const SEVER_SIDE_TRIGGER_BOOLEAN_FIELDS = ["IsConverted", "IsQualified", "IsCustomer"];

export const VARIABLE_RELATED_OBJECT_CUSTOM = [
     { value: "Lead", label: "Lead" },
     { value: "Contract", label: "Contract" },
     { value: "Account", label: "Account" },
     { value: "Opportunity", label: "Opportunity" },
     { value: "Contact", label: "Contact" },
     { value: "Campaign", label: "Campaign" },
     { value: "Quote", label: "Quote" },
     { value: "Order", label: "Order" },
     { value: "Event", label: "Event" },
];

export const VARIABLE_CUSTOM_OBJECT = [
     { value: "Lead", label: "Lead" },
     { value: "Contract", label: "Contract" },
     { value: "Account", label: "Account" },
     { value: "ParentAccount", label: "Parent Account" },
     { value: "Opportunity", label: "Opportunity" },
     { value: "Contact", label: "Contact" },
     { value: "Campaign", label: "Campaign" },
     { value: "Quote", label: "Quote" },
     { value: "Order", label: "Order" },
     { value: "Event", label: "Event" },
];

export const VARIABLE_DATE_DEFAULT_SF = [
     { value: "CreatedDate", label: "CreatedDate", type: 'timestamp' },
     { value: "SystemModstamp", label: "SystemModstamp", type: 'timestamp' },
]

export const VARIABLE_DATE_DEFAULT_CE = [
     { value: "CreatedTimestamp", label: "CreatedTimestamp", type: 'timestamp' },
     { value: "ModifiedTimestamp", label: "ModifiedTimestamp", type: 'timestamp' },
]

export const INFO_TYPE = [
     { value: "property", label: "Property" },
     { value: "metric", label: "Metric" },
]

export const VARIABLE_TRIGGER_FROM_TO = [
     { value: "exav", label: "any value or no value" },
     { value: "vma", label: "a value matching a rule" },
];

export const VARIABLE_TIME_OPTIONS = [
     { value: "00:00", label: "00:00" },
     { value: "00:15", label: "00:15" },
     { value: "00:30", label: "00:30" },
     { value: "00:45", label: "00:45" },
     { value: "01:00", label: "01:00" },
     { value: "01:15", label: "01:15" },
     { value: "01:30", label: "01:30" },
     { value: "01:45", label: "01:45" },
     { value: "02:00", label: "02:00" },
     { value: "02:15", label: "02:15" },
     { value: "02:30", label: "02:30" },
     { value: "02:45", label: "02:45" },
     { value: "03:00", label: "03:00" },
     { value: "03:15", label: "03:15" },
     { value: "03:30", label: "03:30" },
     { value: "03:45", label: "03:45" },
     { value: "04:00", label: "04:00" },
     { value: "04:15", label: "04:15" },
     { value: "04:30", label: "04:30" },
     { value: "04:45", label: "04:45" },
     { value: "05:00", label: "05:00" },
     { value: "05:15", label: "05:15" },
     { value: "05:30", label: "05:30" },
     { value: "05:45", label: "05:45" },
     { value: "06:00", label: "06:00" },
     { value: "06:15", label: "06:15" },
     { value: "06:30", label: "06:30" },
     { value: "06:45", label: "06:45" },
     { value: "07:00", label: "07:00" },
     { value: "07:15", label: "07:15" },
     { value: "07:30", label: "07:30" },
     { value: "07:45", label: "07:45" },
     { value: "08:00", label: "08:00" },
     { value: "08:15", label: "08:15" },
     { value: "08:30", label: "08:30" },
     { value: "08:45", label: "08:45" },
     { value: "09:00", label: "09:00" },
     { value: "09:15", label: "09:15" },
     { value: "09:30", label: "09:30" },
     { value: "09:45", label: "09:45" },
     { value: "10:00", label: "10:00" },
     { value: "10:15", label: "10:15" },
     { value: "10:30", label: "10:30" },
     { value: "10:45", label: "10:45" },
     { value: "11:00", label: "11:00" },
     { value: "11:15", label: "11:15" },
     { value: "11:30", label: "11:30" },
     { value: "11:45", label: "11:45" },
     { value: "12:00", label: "12:00" },
     { value: "12:15", label: "12:15" },
     { value: "12:30", label: "12:30" },
     { value: "12:45", label: "12:45" },
     { value: "13:00", label: "13:00" },
     { value: "13:15", label: "13:15" },
     { value: "13:30", label: "13:30" },
     { value: "13:45", label: "13:45" },
     { value: "14:00", label: "14:00" },
     { value: "14:15", label: "14:15" },
     { value: "14:30", label: "14:30" },
     { value: "14:45", label: "14:45" },
     { value: "15:00", label: "15:00" },
     { value: "15:15", label: "15:15" },
     { value: "15:30", label: "15:30" },
     { value: "15:45", label: "15:45" },
     { value: "16:00", label: "16:00" },
     { value: "16:15", label: "16:15" },
     { value: "16:30", label: "16:30" },
     { value: "16:45", label: "16:45" },
     { value: "17:00", label: "17:00" },
     { value: "17:15", label: "17:15" },
     { value: "17:30", label: "17:30" },
     { value: "17:45", label: "17:45" },
     { value: "18:00", label: "18:00" },
     { value: "18:15", label: "18:15" },
     { value: "18:30", label: "18:30" },
     { value: "18:45", label: "18:45" },
     { value: "19:00", label: "19:00" },
     { value: "19:15", label: "19:15" },
     { value: "19:30", label: "19:30" },
     { value: "19:45", label: "19:45" },
     { value: "20:00", label: "20:00" },
     { value: "20:15", label: "20:15" },
     { value: "20:30", label: "20:30" },
     { value: "20:45", label: "20:45" },
     { value: "21:00", label: "21:00" },
     { value: "21:15", label: "21:15" },
     { value: "21:30", label: "21:30" },
     { value: "21:45", label: "21:45" },
     { value: "22:00", label: "22:00" },
     { value: "22:15", label: "22:15" },
     { value: "22:30", label: "22:30" },
     { value: "22:45", label: "22:45" },
     { value: "22:00", label: "23:00" },
     { value: "23:15", label: "23:15" },
     { value: "23:30", label: "23:30" },
     { value: "23:45", label: "23:45" }
];

export const SUB_FILTER = {
     enrichType : [
          { value: 'b2b', label: 'b2b' }, 
          { value: 'b2c', label: 'b2c' }, 
          { value: 'anySubField', label: 'any sub field' },
          { value: 'allSubField', label: 'all sub field' } 
     ],
     email: [
          { value: 'email', label: 'Email' }, 
          { value: 'valid', label: 'Valid' }, 
          { value: 'qualityScore', label: 'Quality Score' }, 
          { value: 'emailType', label: 'Email Type' },
          { value: 'roleEmail', label: 'Role Email' }, 
          { value: 'catchAllEmail', label: 'Catch All Email' }, 
          { value: 'disposableEmail', label: 'Disposable Email' }, 
          { value: 'ieon', label: 'Is Empty' }, 
          { value: 'ine', label: 'Is Not Empty' }
     ],
     emailList: [
          { value: 'email', label: 'Email' }, 
          { value: 'valid', label: 'Valid' }, 
          { value: 'qualityScore', label: 'Quality Score' },
          { value: 'emailType', label: 'Email Type' }, 
          { value: 'roleEmail', label: 'Role Email' }, 
          { value: 'catchAllEmail', label: 'Catch All Email' }, 
          { value: 'disposableEmail', label: 'Disposable Email' }, 
          { value: 'primary', label: 'Primary' }, 
          { value: 'currentB2BRole', label: 'Current B2B Role' }, 
     ],
     currentCompany: [
          { value: 'companyName', label: 'Company Name' }, 
          { value: 'domain', label: 'Domain' }, 
          { value: 'currentPrimaryIndustry', label: 'Current Primary Industry' },
          { value: 'currentRevenue', label: 'Current Revenue' }, 
          { value: 'ieon', label: 'Is Empty' }, 
          { value: 'ine', label: 'Is Not Empty' } 
     ],
     relatedCompanies: [
          { value: 'companyName', label: 'Company Name' },
          { value: 'domain', label: 'Domain' },
          { value: 'primaryIndustry', label: 'Primary Industry' },
          { value: 'revenue', label: 'Revenue' },
          { value: 'primary', label: 'Primary' },
          { value: 'currentB2BRole', label: 'Current B2B Role' },
     ],
     volunteering: [
          { value: 'companyName', label: 'Company Name' },
          { value: 'role', label: 'Role' },
          { value: 'summary', label: 'Summary' },
          { value: 'currentRole', label: 'Current Role' },
          { value: 'startDate', label: 'Start Date' },
          { value: 'endDate', label: 'End Date' },
     ],
     certifications: [
          { value: 'title', label: 'Title' },
          { value: 'summary', label: 'Summary' },
          { value: 'companyName', label: 'Company Name' },
          { value: 'month', label: 'Month' },
          { value: 'year', label: 'Year' },
     ],
     education: [
          { value: 'schoolName', label: 'School Name' },
          { value: 'fieldOfStudy', label: 'Field of Study' },
          { value: 'degree', label: 'Degree' },
          { value: 'startYear', label: 'Start Year' },
          { value: 'endYear', label: 'End Year' },
     ],
     projects: [
          { value: 'title', label: 'Title' },
          { value: 'summary', label: 'Summary' },
          { value: 'url', label: 'URL' },
     ],
     recommendations: [
          { value: 'recommenderName', label: 'Recommender Name' },
          { value: 'recommendation', label: 'Recommendation' },
     ],
     otherRoles: [
          { value: 'companyName', label: 'Company Name' },
          { value: 'domain', label: 'Domain' },
          { value: 'companyLinkedinUrl', label: 'Company LinkedIn URL' },
          { value: 'title', label: 'Title' },
          { value: 'currentRole', label: 'Current Role' },
          { value: 'seniority', label: 'Seniority' },
          { value: 'jobFunctions', label: 'Job Functions' },
          { value: 'employmentType', label: 'Employment Type' },
          { value: 'startDate', label: 'Start Date' },
          { value: 'endDate', label: 'End Date' },
     ],
}

export const KEY_LL = [
     'enrichType', 'name', 'phone', 'phoneList', 'externalId',
     'dataSources', 'revealedZipCode', 'emailType',
     'currentCompanyPrimaryIndustry', 'currentCompanyRevenue',
     'channelFirst', 'channelLast', 'channelPath', 'userSourceFirstPersonCreated',
     'userSourcePathPersonCreated', 'userSourceLastPersonCreated',
     'landingPageOriginPath', 'exitPageOriginPath', 'referrerHostname',
     'referrerOriginPath', 'referrerPath', 'utmSource', 'utmMedium',
     'utmCampaign', 'utmTerm', 'utmContent', 'userSourceFirst',
     'userSourcePath', 'userSourceLast',
     'industries', 'linkedinSummary', 'skills'
]

export const KEY_LL_CHECKLIST = [
     'dataSources', 'currentCompanyPrimaryIndustry', 
     'currentCompanyRevenue', 'channelFirst', 'channelLast', 
     'channelPath', 'userSourceFirstPersonCreated', 'userSourcePathPersonCreated',
     'userSourceLastPersonCreated', 'userSourceFirst', 'userSourcePath',
     'userSourceLast', 'industries'
]

export const KEY_NOT_EMPTY = [
     'industries', 'companyCountry', 'companyState'
]
